import React from 'react';
import { cn } from '../../lib/utils';

interface DocumentPrintLayoutProps {
  organization: any;
  document: any;
  contact: any;
  type: 'invoice' | 'quote' | 'delivery_note' | 'purchase_order' | 'bill';
  language?: string;
}

export function DocumentPrintLayout({
  organization,
  document: doc,
  contact,
  type,
  language = 'en'
}: DocumentPrintLayoutProps) {
  const isAr = language === 'ar';

  // Documents can come from Firestore or the local API, where optional text
  // fields may occasionally be stored as JSON objects. Never pass those
  // objects directly to React as children.
  const displayText = (value: unknown, fallback = ''): string => {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object') {
      const record = value as Record<string, unknown>;
      const nestedText = record.name ?? record.label ?? record.value ?? record.address;
      if (typeof nestedText === 'string' || typeof nestedText === 'number') return String(nestedText);
      try { return JSON.stringify(value); } catch { return fallback; }
    }
    return fallback;
  };

  const displayNumber = (value: unknown, fallback = 0): number => {
    const number = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(number) ? number : fallback;
  };
  
  const getDocumentTitle = () => {
    switch (type) {
      case 'invoice': return isAr ? 'فاتورة' : 'INVOICE';
      case 'quote': return isAr ? 'عرض سعر' : 'QUOTATION';
      case 'delivery_note': return isAr ? 'إشعار توصيل' : 'DELIVERY NOTE';
      case 'purchase_order': return isAr ? 'أمر شراء' : 'PURCHASE ORDER';
      case 'bill': return isAr ? 'فاتورة شراء' : 'BILL';
      default: return '';
    }
  };

  const formatDate = (dateValue: unknown) => {
    if (!dateValue) return '';
    try {
      const value = dateValue as { seconds?: number; nanoseconds?: number };
      const date = typeof value === 'object' && typeof value.seconds === 'number'
        ? new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1e6)
        : new Date(dateValue as string | number | Date);
      return date.toLocaleDateString(isAr ? 'ar-EG' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return displayText(dateValue);
    }
  };

  return (
    <div className={cn(
      "print-document w-full max-w-[800px] mx-auto bg-white p-12 text-[#141414] font-sans print:p-0",
      isAr ? "text-right" : "text-left"
    )} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex justify-between items-start mb-12 border-b border-[#141414]/10 pb-8">
        <div className="space-y-2">
          {organization?.logo && (
            <img src={organization.logo} alt="Logo" className="h-16 w-auto object-contain mb-4" />
          )}
          <h1 className="text-xl font-black uppercase tracking-tight">{organization?.name || 'Your Company Name'}</h1>
          <div className="text-sm opacity-60 space-y-0.5 whitespace-pre-line">
            {displayText(organization?.address, 'Company Address\nCity, Country')}
            {organization?.phone && <p>{isAr ? 'هاتف: ' : 'Phone: '} {displayText(organization.phone)}</p>}
            {organization?.email && <p>{isAr ? 'بريد: ' : 'Email: '} {displayText(organization.email)}</p>}
            {organization?.vatNumber && <p>{isAr ? 'الرقم الضريبي: ' : 'VAT: '} {displayText(organization.vatNumber)}</p>}
          </div>
        </div>
        <div className="text-right flex flex-col items-end">
          <h2 className="text-4xl font-black mb-4 text-[#141414]">{getDocumentTitle()}</h2>
          <div className="text-sm space-y-1">
            <p className="flex gap-4 justify-end">
              <span className="opacity-40 font-bold uppercase tracking-widest">{isAr ? 'الرقم' : 'Number'}</span>
              <span className="font-bold">{displayText(doc.number, displayText(doc.id)?.slice(-8).toUpperCase())}</span>
            </p>
            <p className="flex gap-4 justify-end">
              <span className="opacity-40 font-bold uppercase tracking-widest">{isAr ? 'التاريخ' : 'Date'}</span>
              <span className="font-bold">{formatDate(doc.date)}</span>
            </p>
            {doc.dueDate && (
              <p className="flex gap-4 justify-end">
                <span className="opacity-40 font-bold uppercase tracking-widest">{isAr ? 'تاريخ الاستحقاق' : 'Due Date'}</span>
                <span className="font-bold">{formatDate(doc.dueDate)}</span>
              </p>
            )}
            {doc.reference && (
              <p className="flex gap-4 justify-end">
                <span className="opacity-40 font-bold uppercase tracking-widest">{isAr ? 'المرجع' : 'Reference'}</span>
                <span className="font-bold">{displayText(doc.reference)}</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Customer Info */}
      <div className="grid grid-cols-2 gap-12 mb-12">
        <div className="space-y-2">
          <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-[#141414]/40 border-b border-[#141414]/10 pb-1 mb-3">
            {isAr ? 'إلى' : 'BILL TO'}
          </h3>
          <p className="font-bold text-lg">{displayText(contact?.name, displayText(doc.contactName, isAr ? 'عميل مقدر' : 'Valued Customer'))}</p>
          <div className="text-sm opacity-60 space-y-0.5 whitespace-pre-line">
            {displayText(contact?.address, displayText(doc.billingAddress, isAr ? 'لا يوجد عنوان' : 'No address provided'))}
            {contact?.phone && <p>{displayText(contact.phone)}</p>}
            {contact?.email && <p>{displayText(contact.email)}</p>}
            {contact?.vatNumber && <p>{isAr ? 'الرقم الضريبي للمستلم: ' : 'Customer VAT: '} {displayText(contact.vatNumber)}</p>}
          </div>
        </div>
        
        {doc.deliveryAddress && (
          <div className="space-y-2">
            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-[#141414]/40 border-b border-[#141414]/10 pb-1 mb-3">
              {isAr ? 'عنوان الشحن' : 'SHIP TO'}
            </h3>
            <p className="text-sm opacity-60 whitespace-pre-line leading-relaxed">
              {displayText(doc.deliveryAddress)}
            </p>
          </div>
        )}
      </div>

      {/* Table */}
      <table className="w-full mb-12 border-collapse">
        <thead>
          <tr className="border-b-2 border-[#141414]">
            <th className="py-3 text-left text-[10px] font-black uppercase tracking-widest opacity-40">{isAr ? 'الوصف' : 'DESCRIPTION'}</th>
            <th className="py-3 text-right text-[10px] font-black uppercase tracking-widest opacity-40">{isAr ? 'الكمية' : 'QUANTITY'}</th>
            <th className="py-3 text-right text-[10px] font-black uppercase tracking-widest opacity-40">{isAr ? 'سعر الوحدة' : 'UNIT PRICE'}</th>
            <th className="py-3 text-right text-[10px] font-black uppercase tracking-widest opacity-40">{isAr ? 'الضرائب' : 'TAXES'}</th>
            <th className="py-3 text-right text-[10px] font-black uppercase tracking-widest opacity-40">{isAr ? 'المبلغ' : 'AMOUNT'}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#141414]/10">
          {(doc.items || []).map((item: any, idx: number) => (
            <tr key={idx}>
              <td className="py-4">
                <p className="font-bold text-sm">{displayText(item.name, displayText(item.item, isAr ? 'عنصر بدون اسم' : 'Unnamed Item'))}</p>
                {item.description && <p className="text-[10px] opacity-50 mt-0.5">{displayText(item.description)}</p>}
                {item.lotNumber && <p className="text-[9px] font-bold mt-1 uppercase text-[#141414]/40">Lot: {displayText(item.lotNumber)}</p>}
              </td>
              <td className="py-4 text-right text-sm font-medium">{displayNumber(item.quantity || item.quantityShipped)}</td>
              <td className="py-4 text-right text-sm font-medium">
                {type !== 'delivery_note' ? displayNumber(item.unitPrice).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}
              </td>
              <td className="py-4 text-right text-xs opacity-50">
                {type !== 'delivery_note' ? displayText(item.taxRate, '0%') : '-'}
              </td>
              <td className="py-4 text-right text-sm font-bold">
                {type !== 'delivery_note' ? displayNumber(item.amount || item.total).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      {type !== 'delivery_note' && (
        <div className="flex justify-end mb-12">
          <div className="w-1/2 space-y-3">
            <div className="flex justify-between py-2 border-b border-[#141414]/5">
              <span className="text-sm opacity-40 font-bold uppercase tracking-widest">{isAr ? 'المجموع الفرعي' : 'SUBTOTAL'}</span>
              <span className="font-bold">
                {displayText(doc.currency)} {displayNumber(doc.subTotal ?? (displayNumber(doc.totalAmount) - displayNumber(doc.taxAmount))).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
            {(doc.taxAmount > 0 || doc.taxDetails?.length > 0) && (
              <div className="flex justify-between py-2 border-b border-[#141414]/5">
                <span className="text-sm opacity-40 font-bold uppercase tracking-widest">{isAr ? 'الضرائب' : 'TAXES'}</span>
                <span className="font-bold">
                  {displayText(doc.currency)} {displayNumber(doc.taxAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}
            <div className="flex justify-between py-4 bg-[#141414] text-white px-4 rounded-xl">
              <span className="text-sm font-black uppercase tracking-widest">{isAr ? 'الإجمالي' : 'TOTAL'}</span>
              <span className="text-lg font-black">
                {displayText(doc.currency)} {displayNumber(doc.totalAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
            {doc.paymentReceived > 0 && (
              <div className="flex justify-between py-2 px-4 italic opacity-60">
                <span className="text-sm">{isAr ? 'المبلغ المدفوع' : 'Amount Paid'}</span>
                <span className="font-bold text-sm">
                  ({displayText(doc.currency)} {displayNumber(doc.paymentReceived).toLocaleString(undefined, { minimumFractionDigits: 2 })})
                </span>
              </div>
            )}
            {doc.paymentReceived > 0 && (
              <div className="flex justify-between py-2 px-4 border-t border-[#141414] mt-2">
                <span className="text-sm font-bold uppercase tracking-widest">{isAr ? 'الرصيد المستحق' : 'AMOUNT DUE'}</span>
                <span className="font-black text-xl">
                  {displayText(doc.currency)} { (displayNumber(doc.totalAmount) - displayNumber(doc.paymentReceived)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Signature section for delivery notes */}
      {type === 'delivery_note' && doc.status === 'Delivered' && (
        <div className="grid grid-cols-2 gap-12 mt-12 border-t border-[#141414]/10 pt-12">
          <div className="space-y-4">
            <p className="text-[10px] font-black uppercase tracking-widest opacity-40">{isAr ? 'توقيع المستلم' : 'RECEIVER SIGNATURE'}</p>
            {doc.signatureUrl ? (
              <img src={doc.signatureUrl} alt="Signature" className="h-24 w-auto grayscale" />
            ) : (
              <div className="h-24 border-b-2 border-[#141414]/10"></div>
            )}
            <p className="font-bold text-sm">{displayText(doc.receiverName)}</p>
            <p className="text-[10px] opacity-40">{isAr ? 'تاريخ الاستلام: ' : 'Delivered on: '} {formatDate(doc.deliveredAt)}</p>
          </div>
          <div className="space-y-4">
            <p className="text-[10px] font-black uppercase tracking-widest opacity-40">{isAr ? 'توقيع التسليم' : 'DELIVERY AUTHORIZATION'}</p>
            <div className="h-24 border-b-2 border-[#141414]/10 flex items-end pb-2">
               <p className="text-xs italic opacity-40 font-serif">Electronic System Signature</p>
            </div>
            <p className="font-bold text-sm">{doc.assignedPersonnelName || 'System Verified'}</p>
          </div>
        </div>
      )}

      {/* QR Code / Payment Info */}
      <div className="mt-12 pt-12 border-t border-[#141414]/10 grid grid-cols-2 gap-12">
        <div className="space-y-3">
          <h4 className="text-[10px] font-black uppercase tracking-widest opacity-40">{isAr ? 'ملاحظات وشروط' : 'NOTES & TERMS'}</h4>
          <p className="text-[11px] leading-relaxed opacity-60">
            {displayText(doc.termsAndConditions, displayText(doc.notes, isAr ? 'شكرا لتعاملك معنا' : 'Thank you for your business. Please pay within the due date.'))}
          </p>
        </div>
        <div className="flex flex-col items-end gap-3 text-right">
           <div className="bg-white p-2 border border-[#141414]/5 rounded-xl shadow-sm">
              {/* Using a placeholder for simplicity, standard QR can be added if needed */}
              <div className="w-24 h-24 bg-[#141414]/5 rounded flex items-center justify-center">
                 <span className="text-[10px] opacity-30 font-bold uppercase tracking-tighter">Secure QR</span>
              </div>
           </div>
           <p className="text-[9px] opacity-30 uppercase font-black tracking-[0.3em]">
             {isAr ? 'فاتورة إلكترونية معتمدة' : 'CERTIFIED ELECTRONIC DOCUMENT'}
           </p>
        </div>
      </div>

      {/* Footer Branding Area */}
      <div className="mt-24 pt-8 border-t border-[#141414]/10 text-center">
        <p className="text-[10px] opacity-30 uppercase font-black tracking-[0.4em]">
          {displayText(organization?.name, 'Your Company')} • {displayText(organization?.website, 'www.example.com')}
        </p>
      </div>
    </div>
  );
}
